import { fnv1a, isChannelPlaylistId, itemSeconds } from "../../shared/signage";
import type {
  SignageItem,
  SignagePlaylist,
  SignageTimeline,
} from "../../shared/types/signage";

/**
 * The legacy autoplayer schedule payload, built from Foyer's own timeline.
 *
 * ## Why this module exists
 *
 * There are too many fielded signage autoplayers (Tizen WGT / Android) to
 * replace them, and re-pointing one is a one-line change to a manifest we own -
 * so the migration path for a hotel's signage is "point the existing player at
 * Foyer", not "visit every screen". That makes `/api/signage` a *migration
 * lever*, not a compatibility tax. See docs/legacy-signage-facade.md.
 *
 * ## The one rule
 *
 * **Every lie the legacy protocol tells is re-applied HERE and nowhere else.**
 * Foyer's model and its own API speak real UTC, carry an explicit `endsAt` on
 * every interval, and hash a revision over everything that renders. The player
 * on the wall expects none of that. This file is the whole of the boundary; if
 * a legacy-shaped concept leaks past it into `@shared/signage` or the store,
 * the port has failed.
 *
 * Pure: no Mongo, no clock, no `Date.now()`. `timeline` and `commands` are
 * resolved by the caller, so every shape below is unit-testable without a
 * database.
 */

/* -------------------------------------------------------------------------- */
/* The legacy wire types - copied from nenda-management-admin, not invented    */
/* -------------------------------------------------------------------------- */

/** `ScheduleSlotType` upstream. Capitalised; the client's enum matches. */
export type LegacySlotType = "Content" | "Signage" | "Ad";

/** `ContentScheduleType` upstream. Lowercase - a different casing convention
 *  in the same payload, which is upstream's and therefore ours. */
export type LegacyContentSource = "scheduled" | "override" | "default";

/** The two commands the fielded player understands. Anything else is dropped. */
export type LegacyCommand = "reboot" | "logout";

/**
 * `IAdPermission`. The player dereferences
 * `adPermissions.external.fullTakeoverAllowed` inside an XState guard on every
 * 500 ms tick, with no optional chaining - so this object, both sub-objects and
 * all four booleans must be present on EVERY content entry or the player throws
 * twice a second.
 */
export interface LegacyAdPermissions {
  external: { fullTakeoverAllowed: boolean; bannerAllowed: boolean };
  signage: { fullTakeoverAllowed: boolean; bannerAllowed: boolean };
}

/** `IAssetWithDuration`, reduced to the keys the player actually reads. */
export interface LegacyAsset {
  /** The player falls back `item.id || item._id`. Upstream sends `_id`. */
  _id: string;
  name: string;
  /** Playback URL. Must be ABSOLUTE - the player has no base to resolve against. */
  locator: string;
  type: "image" | "video";
  source: "Nenda";
  /** Never 0 and never absent: `items[i].durationInSeconds` is dereferenced
   *  without a guard when computing playback times. */
  durationInSeconds: number;
}

/** `ScheduledSignagePlaylist`. */
export interface LegacySignagePlaylist {
  id: string;
  name: string;
  isSoundEnabled: boolean;
  items: LegacyAsset[];
  adPermissions: LegacyAdPermissions;
}

/** `ITimeSchedule` entry as emitted by `generateContentSchedule`. */
export interface LegacyContentSlot {
  name: LegacyContentSource;
  type: LegacySlotType;
  /** sha256 hex - a key into `playlists`. */
  playlist: string;
}

/** `ITimeSchedule` entry as emitted by `generateHour`. Note: no `name` key. */
export interface LegacySlot {
  playlist: string;
  type: LegacySlotType;
}

/** `IScheduledPlaylists` entry. */
export type LegacyPlaylistEntry =
  | { type: "Content"; signagePlaylist: string }
  | { type: "Content"; channels: string[] }
  | { type: "Signage"; assets: LegacyAsset[] };

/** `ISchedule`. Key order below matches upstream's return literal. */
export interface LegacySchedule {
  /**
   * `ScheduledChannel[]`. **Always empty from Foyer.**
   *
   * Upstream has two near-identical loop entities - `ContentChannel` (Nenda's
   * curated editorial loops) and `SignagePlaylist` (a customer's own files) -
   * joined by a polymorphic ref. Foyer merged them into ONE entity whose
   * curated/own distinction falls out of the media layer, so there is nothing
   * to put here. The player handles `[]` (it `.map`s over it); it would crash
   * on `undefined`, which is why the key is always present.
   */
  channels: never[];
  signagePlaylists: LegacySignagePlaylist[];
  contentSchedule: Record<string, LegacyContentSlot>;
  slotSchedule: Record<string, LegacySlot>;
  playlists: Record<string, LegacyPlaylistEntry>;
  options: { isSoundEnabled: boolean };
  commands: LegacyCommand[];
}

/**
 * Ads are not a Foyer product, so no screen we serve may be taken over by one.
 *
 * Upstream's fallback for a screen with no configured permissions is all-TRUE.
 * Ours is all-FALSE, deliberately: we emit no `Ad` markers at all, so a
 * permissive value would only describe inventory that never arrives. See the
 * revenue note in docs/legacy-signage-facade.md.
 */
export const NO_AD_PERMISSIONS: LegacyAdPermissions = {
  external: { fullTakeoverAllowed: false, bannerAllowed: false },
  signage: { fullTakeoverAllowed: false, bannerAllowed: false },
};

/**
 * Per-playlist ad permissions for a content-loop entry.
 *
 * `external` stays all-false for the reason {@link NO_AD_PERMISSIONS} gives -
 * no ad product, so no inventory to describe. `signage.fullTakeoverAllowed`
 * is NOT a constant: it is what tells a player "a campaign break may take you
 * over", and hardcoding it false was a real bug, not a conservative default -
 * a player dereferencing this object on a normal loop would never run a
 * single campaign break, silently. The rule mirrors `breakMayInterrupt`
 * (`@shared/signage.ts`) exactly, and for the same reason: a live channel
 * fails closed (an ad over live broadcast is the wrong failure mode), an
 * ordinary catalogue loop does not.
 */
function adPermissionsFor(playlistId: string): LegacyAdPermissions {
  return {
    external: { fullTakeoverAllowed: false, bannerAllowed: false },
    signage: {
      fullTakeoverAllowed: !isChannelPlaylistId(playlistId),
      bannerAllowed: false,
    },
  };
}

/** How far ahead a legacy schedule reaches. Upstream's
 *  `SCHEDULE_NUMBER_OF_HOURS`, and the player polls every 30 s. */
export const LEGACY_HORIZON_HOURS = 2;

/* -------------------------------------------------------------------------- */
/* The wall-clock-as-UTC lie                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Premise-local wall-clock time, branded `Z`. **A deliberate lie.**
 *
 * "10AM in Tokyo => 10AM Zulu, 10AM in Berlin => 10AM Zulu". Upstream computes
 * it by adding the zone offset to a real instant; the player mirrors the trick
 * with `getLocalDateTimeAsZulu()` and then matches `slotSchedule` keys by
 * EXACT STRING EQUALITY, sampled twice a second. So this is not a display
 * format - it is the join key, and every character of it is load-bearing:
 *
 * - exactly `YYYY-MM-DDTHH:mm:ss.000Z`, 24 characters
 * - milliseconds are always the literal `.000` (real ones would never match)
 * - always `Z`, never `+00:00`
 * - second granularity, TRUNCATED not rounded - the player asks "the latest
 *   content key at or before now", and a rounded-up key is not yet due
 *
 * Getting this wrong is silent: the screen plays its background loop forever
 * and no campaign break ever fires. There is no error anywhere.
 *
 * Rendered straight out of `Intl` in the target zone rather than by offset
 * arithmetic, so DST needs no special case: whatever the zone says the wall
 * clock reads is what we stamp.
 */
export function toWallClockZ(instant: Date, timeZone: string): string {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const p = Object.fromEntries(
    fmt.formatToParts(instant).map((x) => [x.type, x.value])
  ) as Record<string, string>;
  // Some ICU versions render midnight as hour "24" - the same trap
  // `@shared/signage` guards in `zoneOffsetMs`.
  const hour = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}:${p.second}.000Z`;
}

/**
 * The top of the premise-local hour containing `instant`, as a real UTC Date.
 *
 * The legacy window always starts on the hour, and that is worth reproducing
 * for a reason beyond fidelity: it makes the emitted keys STABLE across polls.
 * Resolving from "now" instead would move every key by ~30 s on each of the
 * player's polls, and a content key that changes is a content change - the loop
 * would restart from its first slide twice a minute.
 */
export function startOfLocalHour(instant: Date, timeZone: string): Date {
  const stamp = toWallClockZ(instant, timeZone);
  const minutes = Number(stamp.slice(14, 16));
  const seconds = Number(stamp.slice(17, 19));
  return new Date(instant.getTime() - (minutes * 60 + seconds) * 1000);
}

/* -------------------------------------------------------------------------- */
/* Playlist identity                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The content-addressed id that keys the `playlists` map.
 *
 * There is no ETag, no version and no push on this protocol - this hash is the
 * ONLY change signal that exists, so what it covers decides what can reach a
 * screen. Upstream hashes asset locators alone, which means an operator editing
 * a slide's duration produces an identical id and the edit never arrives. Every
 * rendered property is folded in here instead, matching the rule `revision`
 * already follows in `@shared/signage`.
 *
 * Delimited and kind-tagged because the same flat map holds loop playlists and
 * campaign-break playlists: upstream's bare concatenation of ids lets two
 * different playlists collide onto one key, and the merge order silently
 * decides which one plays.
 *
 * `fnv1a` (from `@shared/signage`, the same primitive `revisionOf` already
 * uses there), not `node:crypto`'s sha256: this adapter is meant to be
 * callable client-side too, in-process from the TV app itself (the "surgical
 * port" plan), and `node:crypto` doesn't exist in a browser. Not a security
 * primitive either way - just a change detector for a handful of playlists
 * per screen's schedule, where 32-bit collision risk is negligible.
 */
export function legacyPlaylistId(
  kind: "loop" | "break",
  ownerId: string,
  items: LegacyAsset[]
): string {
  const body = items
    .map((i) => `${i.locator}|${i.durationInSeconds}|${i.type}`)
    .join("\n");
  return fnv1a(`${kind}\n${ownerId}\n${body}`);
}

/* -------------------------------------------------------------------------- */
/* Asset mapping                                                               */
/* -------------------------------------------------------------------------- */

/** `/foyer-assets/x.jpg` -> `https://host/foyer-assets/x.jpg`. Already-absolute
 *  urls pass through, so a future CDN-hosted asset needs no change here. */
function absolutise(assetUrl: string, baseUrl: string): string {
  if (/^https?:\/\//i.test(assetUrl)) return assetUrl;
  return `${baseUrl.replace(/\/+$/, "")}${assetUrl.startsWith("/") ? "" : "/"}${assetUrl}`;
}

/** A readable name for the fleet's logs, derived from the file. Upstream sends
 *  the operator's asset name; Foyer's signage items carry no name of their own. */
function assetName(assetUrl: string): string {
  const last = assetUrl.split("?")[0]!.split("/").pop() ?? "";
  return decodeURIComponent(last) || "asset";
}

/**
 * One `SignageItem` as the player expects it.
 *
 * `durationSeconds: 0` means "play to its natural end" in Foyer - an affordance
 * this protocol does not have, and the player would throw on a missing
 * duration. `itemSeconds()` resolves it against the playlist default, which is
 * the same substitution the CMS timeline makes.
 */
/**
 * The items a fielded autoplayer can render at all.
 *
 * `LegacyAsset.type` is `"image" | "video"` — copied from the vendor, not
 * invented — and `transformSchedule` dereferences every item unguarded on the
 * player, so a third value goes onto the wire as a type the XState machine has
 * no branch for. Foyer-only kinds (`url`, and template instances next) are
 * therefore DROPPED here rather than downgraded into something they are not: a
 * still frame standing in for a live page is a lie a wall cannot correct.
 *
 * ⚠️ **Filtering can empty a loop, and an empty loop CRASHES the player** on
 * `items[currentIndex]`. Both call sites already skip a zero-item playlist,
 * which emits no schedule key for that interval — a GAP, which this protocol
 * cannot express, so the screen keeps playing its previous loop. That is the
 * documented and correct behaviour (see `docs/legacy-signage-facade.md`), and
 * it is why the filter must stay in front of those guards rather than anywhere
 * later. Do not "fix" a resulting empty playlist by emitting it.
 *
 * The operator is told the same thing on the authoring surface: the playlist
 * editor labels these items Foyer-only, per the standing rule that no CMS
 * control may exist that one client generation silently cannot honour.
 */
export function playableOnLegacy(
  item: SignageItem
): item is SignageItem & { kind: "image" | "video" } {
  return item.kind === "image" || item.kind === "video";
}

function toLegacyAsset(
  item: SignageItem & { kind: "image" | "video" },
  playlist: SignagePlaylist,
  baseUrl: string
): LegacyAsset {
  return {
    _id: item.id,
    name: assetName(item.assetUrl),
    locator: absolutise(item.assetUrl, baseUrl),
    type: item.kind,
    source: "Nenda",
    // Never 0: a zero-length item would make the player's playhead maths
    // divide a loop of length 0.
    durationInSeconds: Math.max(1, Math.round(itemSeconds(item, playlist))),
  };
}

/** A campaign break's items have no parent playlist, so the break's own length
 *  is the fallback when an item declines to declare one. */
function breakItemToLegacyAsset(
  item: SignageItem & { kind: "image" | "video" },
  fallbackSeconds: number,
  baseUrl: string
): LegacyAsset {
  return {
    _id: item.id,
    name: assetName(item.assetUrl),
    locator: absolutise(item.assetUrl, baseUrl),
    type: item.kind,
    source: "Nenda",
    durationInSeconds: Math.max(
      1,
      Math.round(
        item.durationSeconds > 0 ? item.durationSeconds : fallbackSeconds
      )
    ),
  };
}

/** Foyer says `plan`; the wire says `scheduled`. Same concept, different word. */
function toLegacySource(
  source: SignageTimeline["content"][number]["source"]
): LegacyContentSource {
  return source === "plan" ? "scheduled" : source;
}

/* -------------------------------------------------------------------------- */
/* The transform                                                               */
/* -------------------------------------------------------------------------- */

export interface LegacyScheduleInput {
  /** Foyer's resolved timeline. Real UTC in, wall-clock-`Z` out. */
  timeline: SignageTimeline;
  /** Absolute origin the player can fetch assets from, e.g. `https://foyer.nenda.com`. */
  assetBaseUrl: string;
  /** Commands already drained from Foyer's queue by the caller. */
  commands: LegacyCommand[];
}

/**
 * Render a Foyer timeline as the payload a fielded autoplayer expects.
 *
 * ### The one thing this cannot carry: a gap
 *
 * Foyer's timeline emits non-overlapping intervals and a gap is meaningful -
 * "nothing is scheduled, show nothing", which for a lobby display is the
 * correct output rather than an error. The legacy payload has start markers
 * only: an entry runs until the next one, and there is no way to spell "stop".
 * The nearest encoding - an entry pointing at an empty playlist - crashes the
 * player, which indexes `items[currentIndex]` unguarded.
 *
 * So a gap is emitted as NO KEY, and the consequence is stated rather than
 * hidden: **on a legacy player a gap means the previous loop keeps running,
 * where a Foyer client would go dark.** A screen whose plan covers 08:00-20:00
 * plays its evening loop all night. That is a behaviour difference an operator
 * can see, and it is the single strongest argument for moving a site's screens
 * onto the Foyer client once its content is authored here.
 */
export function legacyScheduleFromTimeline(
  input: LegacyScheduleInput
): LegacySchedule {
  const { timeline, assetBaseUrl } = input;
  const tz = timeline.timezone;

  const playlists: Record<string, LegacyPlaylistEntry> = {};
  const contentSchedule: Record<string, LegacyContentSlot> = {};
  const signagePlaylists: LegacySignagePlaylist[] = [];
  const emitted = new Set<string>();

  const byId = new Map(timeline.playlists.map((p) => [p.id, p]));

  /* -- the background loop ---------------------------------------------- */

  for (const interval of timeline.content) {
    const playlist = byId.get(interval.playlistId);
    // The resolver only ever emits intervals whose playlist it materialised,
    // so a miss here is a bug rather than a state - but dropping the interval
    // is the safe failure: an entry pointing at a playlist the player cannot
    // find silently stalls its content machine.
    if (!playlist) continue;

    // Foyer-only kinds are dropped, never downgraded. See `playableOnLegacy`.
    const items = playlist.items
      .filter(playableOnLegacy)
      .map((i) => toLegacyAsset(i, playlist, assetBaseUrl));
    // An empty loop is not renderable by this player (unguarded `items[0]`),
    // and emitting it would crash the screen rather than leave it as it was.
    if (items.length === 0) continue;

    const hash = legacyPlaylistId("loop", playlist.id, items);

    if (!emitted.has(hash)) {
      emitted.add(hash);
      // Both content-derived variants are typed "Content" upstream, even the
      // signage-playlist one. The player switches on `signagePlaylist` being
      // present, not on this.
      playlists[hash] = { type: "Content", signagePlaylist: playlist.id };
      signagePlaylists.push({
        id: playlist.id,
        name: playlist.name,
        isSoundEnabled: playlist.soundEnabled && timeline.soundEnabled,
        items,
        adPermissions: adPermissionsFor(playlist.id),
      });
    }

    contentSchedule[toWallClockZ(new Date(interval.startsAt), tz)] = {
      name: toLegacySource(interval.source),
      // "Signage" because every Foyer loop is a SignagePlaylist upstream -
      // there is no ContentChannel lane on this side.
      type: "Signage",
      playlist: hash,
    };
  }

  /* -- campaign breaks --------------------------------------------------- */

  const slotSchedule: Record<string, LegacySlot> = {};

  for (const brk of timeline.breaks) {
    const items = brk.items
      .filter(playableOnLegacy)
      .map((i) => breakItemToLegacyAsset(i, brk.durationSeconds, assetBaseUrl));
    if (items.length === 0) continue;

    const hash = legacyPlaylistId("break", brk.campaignIds.join(","), items);
    playlists[hash] = { type: "Signage", assets: items };
    slotSchedule[toWallClockZ(new Date(brk.startsAt), tz)] = {
      playlist: hash,
      type: "Signage",
    };
  }

  // NOTE the absent lane: upstream also writes `{playlist:"",type:"Ad"}` at the
  // fixed ad-marker minutes inside business hours, which is the player's cue to
  // fetch a real ad just-in-time. Foyer sells no ad inventory, so it emits no
  // ad markers, and a screen served from here never calls `/adslot`.

  return {
    channels: [],
    signagePlaylists,
    contentSchedule,
    slotSchedule,
    playlists,
    options: { isSoundEnabled: timeline.soundEnabled },
    commands: input.commands,
  };
}
